import { OrgChartData } from '@modules/App/pages/OrgChartPage/interfaces/OrgChartData';
import { OrgChartTree } from '../interfaces/OrgChartTreeNode';
import { HappinessReaction } from '../interfaces/HappinessReaction';
import {
  DEPARTMENT_ORG_CHART_ROOT_NODE_ID,
  PEOPLE_ORG_CHART_ROOT_NODE_POSITION,
  VIRTUAL_ORG_CHART_DEPARTMENT_IDS,
} from '../constants/constants';
import { Role } from '@shared/enums/role';
import { ProjectRole } from '@shared/enums/projectRole';
import { FilterInfoOrgChart } from '@modules/App/pages/OrgChartPage/interfaces/FilterInfoOrgChart';
import { OrgChartFilter } from '../enums/Filters';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import Range from '@shared/interfaces/Range';
import { SortInfo } from '@shared/interfaces/sortInfo';
import { sortData } from '@shared/redux/helpers/sortData';

const getOrgChartDataWithOneRoot = (rootNodeId: string, orgChartData: OrgChartData[]): OrgChartData[] => {
  const idSet = new Set(orgChartData.map((d) => d.id));
  return orgChartData.map((node) => {
    if (!node.parentId || !idSet.has(node.parentId)) {
      return node.id === rootNodeId ? node : { ...node, parentId: rootNodeId };
    }
    return node;
  });
};

export const createOrgChartTree = (orgChartData: OrgChartData[]): OrgChartTree => {
  const tree: OrgChartTree = {};

  for (const d of orgChartData) {
    if (!tree[d.id]) {
      tree[d.id] = {
        children: [],
        teamMembersCount: 0,
        subDepartmentsCount: 0,
        isTeamMember: !!d.position,
        originalId: d.originalId,
        happiness: d.position ? d.happiness : null,
        id: d.id,
        roles: d.roles,
      };
    }
    if (d.parentId) {
      if (!tree[d.parentId]) {
        tree[d.parentId] = {
          children: [],
          teamMembersCount: 0,
          subDepartmentsCount: 0,
          happiness: null,
          id: d.parentId,
          originalId: undefined,
          roles: [],
        };
      }
      tree[d.parentId].children.push(d);
    }
  }

  return tree;
};

const countAggregations = (id: string, tree: OrgChartTree, rootId: string): Set<string> => {
  const node = tree[id];
  const isPerson = node.isTeamMember;
  const members = new Set<string>();

  if (isPerson) {
    members.add(node.originalId || node.id);
  }

  let departments = isPerson ? 0 : 1;
  let sum = node.happiness?.reaction || 0;
  let responded = 0;
  const respondedIds = new Set<string>();

  for (const child of node.children) {
    const childIds = countAggregations(child.id, tree, rootId);
    for (const mid of childIds) members.add(mid);
    departments += tree[child.id].subDepartmentsCount;

    const h = tree[child.id].happiness;
    if (h?.sumHappiness && rootId !== id) {
      sum += h.sumHappiness;
      responded += h.childWithHappinessCount || 1;
      for (const r of h.childrenRespondedIds || []) respondedIds.add(r);
    }
  }

  const happiness: HappinessReaction | null | undefined = sum
    ? {
        reaction: responded ? sum / responded : sum,
        sumHappiness: sum,
        childrenRespondedIds: [...respondedIds],
        childrenRespondedIdsUniqueCount: respondedIds.size,
        childWithHappinessCount: responded,
      }
    : node.happiness;

  Object.assign(node, {
    subDepartmentsCount: departments,
    teamMembersCount: members.size,
    happiness,
  });

  return members;
};

export const addCountAggregationsToOrgChartTree = (rootId: string, tree: OrgChartTree): OrgChartTree => {
  const copy = cloneDeep(tree);
  countAggregations(rootId, copy, rootId);
  return copy;
};

export const getOrgChartTreeData = (
  rawData: OrgChartData[] | null
): { chartData: OrgChartData[]; rootNodeId: string } => {
  if (!rawData || rawData.length === 0) return { chartData: [], rootNodeId: '' };

  const rootNode =
    rawData.find((n) => n.id === DEPARTMENT_ORG_CHART_ROOT_NODE_ID) ||
    rawData.find((n) => n.position === PEOPLE_ORG_CHART_ROOT_NODE_POSITION);

  const rootId = rootNode?.id || '';
  const withRoot = getOrgChartDataWithOneRoot(rootId, rawData);
  const tree = createOrgChartTree(withRoot);
  const withAgg = addCountAggregationsToOrgChartTree(rootId, tree);

  const cleaned = withRoot.filter((d) => d.position !== null || withAgg[d.id].teamMembersCount >= 0);

  const cleanTree = addCountAggregationsToOrgChartTree(rootId, createOrgChartTree(cleaned));

  return {
    chartData: cleaned.map((d) => ({
      ...d,
      teamMembersCount: cleanTree[d.id].teamMembersCount,
      subDepartmentsCount: cleanTree[d.id].subDepartmentsCount,
      children: cleanTree[d.id].children,
      happiness: cleanTree[d.id].happiness,
      roles: cleanTree[d.id].roles,
    })),
    rootNodeId: rootId,
  };
};

export const getOrgChartDataWithoutHiddenElements = (arr: OrgChartData[], toDelete: string[]): OrgChartData[] => {
  const toDeleteSet = new Set(toDelete);

  const isDescendant = (id: string, parentIdSet: Set<string>): boolean => {
    const parent = arr.find((d) => d.id === id)?.parentId;
    if (!parent) return false;
    if (parentIdSet.has(parent)) return true;
    return isDescendant(parent, parentIdSet);
  };

  return arr.filter((d) => !toDeleteSet.has(d.id) && !isDescendant(d.id, toDeleteSet));
};

export const getTheSameNodeButForAnotherParentIfExist = ({
  isDepartmentChart,
  node,
  orgChartData,
  hiddenDepartments,
}: {
  isDepartmentChart: boolean;
  node: OrgChartData;
  orgChartData: OrgChartData[];
  hiddenDepartments: string[];
}): OrgChartData | null => {
  if (!isDepartmentChart || !node || !hiddenDepartments.includes(node?.projectId)) return null;

  for (const item of node.projects ?? []) {
    if (!hiddenDepartments.includes(item.projectId)) {
      const parentProject = orgChartData.find((d) => d.id === item.projectId);
      const sibling = parentProject?.children?.find((d) => d.originalId === node.id);
      if (sibling) return sibling;
    }
  }

  return null;
};

export const getRoles = (chartData: OrgChartData[] | null): ProjectRole[] => {
  if (!chartData) return [];
  const roles = new Set<ProjectRole>();
  for (const item of chartData) {
    for (const role of item.roles || []) {
      roles.add(role);
    }
  }
  return [...roles];
};

const checkIfValueInRange = (value: number, range: Range) => range.from <= value && value <= range.to;

export const applyFiltersFunc = (chartData: OrgChartData[], filters: FilterInfoOrgChart[]): OrgChartData[] => {
  if (!filters.length) return chartData;

  const filtered = chartData
    .filter((item) => !item.originalId)
    .filter((item) =>
      filters.every((filter) => {
        const val = get(item, filter.field);
        if (
          filter.field === OrgChartFilter.HappinessReaction &&
          typeof filter.value === 'object' &&
          filter &&
          filter.value &&
          'from' in filter.value
        ) {
          return val && checkIfValueInRange(val as number, filter.value as Range);
        }
        if (filter.field === OrgChartFilter.Roles) {
          return (val as Role[])?.some((role) => (filter.value as Role[]).includes(role));
        }
        if (filter.field === OrgChartFilter.Happiness) {
          return filter.reverse && val !== filter.value;
        }
        return val === filter.value;
      })
    );

  const ids = new Set(filtered.map((f) => f.id));
  for (const item of filtered) {
    let current = item;
    while (current.parentId) {
      const parent = chartData.find((d) => d.id === current.parentId);
      if (parent && !ids.has(parent.id)) {
        filtered.push(parent);
        ids.add(parent.id);
        current = parent;
      } else {
        break;
      }
    }
  }

  return getOrgChartTreeData(filtered).chartData;
};

export const sortOrgChartData = (chartData: OrgChartData[], sortInfo: SortInfo): OrgChartData[] => {
  const people = sortData(
    chartData.filter((item) => item.position),
    sortInfo
  );
  const departments = sortData(
    chartData.filter((item) => !item.position),
    sortInfo
  );
  const regularDepartments = departments.filter((item) => !VIRTUAL_ORG_CHART_DEPARTMENT_IDS.has(item.id));
  const virtualDepartments = departments.filter((item) => VIRTUAL_ORG_CHART_DEPARTMENT_IDS.has(item.id));

  return [...people, ...regularDepartments, ...virtualDepartments];
};
